import { initLogger } from '@pex/utils';
import { createUseLogger, createLoggerDirective, useAutoPv } from '@pex/utils/logger/vue3';
import AutoPV from '#autoPvPlugin';
import Cookie from 'js-cookie';
import { type App, inject } from 'vue';
import type { Router } from 'vue-router';

import Radar from '#radar';
import { Weblog } from '#weblogger';
import { RouteName } from '@/router';
import { getAdidFromByQuery, getOpenFromByQuery } from '@/utils/index';
import { OrderSourceEnum } from '@/types/cool-plan';
import { FmpEffect } from '#fmp-effect';
import type { LogTypeForDirective, LogTypeForImperativeCall } from '@/common/loggerTypes';
// @ts-expect-error
import EchoPlugin from '@weblogger/plugin-echo';

export const createLoggerUtils = () => {
    const logger = new Weblog(
        {
            logger: import.meta.env.DEV,
            // TODO: 需要环境变量提供后区分下环境
            env: 'production',
            plugins: [
                new AutoPV({
                    autoPVFilter(currentUrl, prevUrl, type) {
                        // 当前 url，前一个 url，pv 子类型 enter|leave|hidden|visible
                        // 离开事件才上报
                        return type === 'leave';
                    },
                }),
                new EchoPlugin(),
            ],
        },
        {
            user_id: Cookie.get('ud') ?? '',
            product_name: Cookie.get('kpn') ?? 'KUAISHOU',
        },
    );

    return initLogger(logger);
};

export const useLogger = createUseLogger<LogTypeForImperativeCall>();

export const vLog = createLoggerDirective<LogTypeForDirective>();

export const useRadar = () => {
    return inject<Radar>('radar');
};

export function initLoggerAndRadar(useSSR: boolean) {
    if (import.meta.env.SSR) {
        return {};
    }
    const loggerUtils = createLoggerUtils();
    const radar = new Radar({
        weblog: loggerUtils.logger,
        // https://radar-plus.corp.kuaishou.com/project/dd5bbb8309/dashboard?env=native&timeRange=Last1Hour
        projectId: '61939975d5',
        sampling: 1,
        lcp: true,
        cls: true,
        fid: true,
        fcp: true,
        customDimensions: {
            product_version: __COMMIT_ID__ ?? '',
            c_dimension1: window?.ENV_INFO?.icfo,
            // eslint-disable-next-line no-underscore-dangle
            c_dimension2: window.__USE_SSR__,
            c_dimension3: 'pkg_pic' + Cookie.get('ug_toufang_pag_pic1'),
        },
    });
    if (useSSR) {
        radar?.fmp((performance?.timing as any)?.ssrFmp);
    }
    return {
        loggerUtils,
        radar,
    };
}

export function installLogger(
    app: App,
    router: Router,
    radar?: Radar,
    loggerUtils?: ReturnType<typeof createLoggerUtils>,
) {
    if (import.meta.env.SSR || !loggerUtils || !radar) {
        return;
    }
    const ssrFmpEffect = new FmpEffect({
        weblog: loggerUtils.logger, //weblog实例，必传
        radar, //radar实例，必传
        options: {
            //ZoneOptions形式的参数
            start: 1000,
            end: 10000,
            range: 500,
        },
        fmp: (performance?.timing as any)?.ssrFmp - performance.timing.navigationStart,
    });

    const fmpEffect = new FmpEffect({
        weblog: loggerUtils.logger, //weblog实例，必传
        radar, //radar实例，必传
        options: {
            //ZoneOptions形式的参数
            start: 2000,
            end: 18000,
            range: 500,
        },
    });

    app.provide('logger', loggerUtils);
    app.provide('radar', radar);
    app.provide('fmpEffect', fmpEffect);
    app.provide('ssrFmpEffect', ssrFmpEffect);

    useAutoPv(
        router,
        loggerUtils!,
        (to) => {
            const query = (to as Record<string, any>).query;
            if (to.name === RouteName.COOL_PLAN.HOME) {
                return {
                    params: {
                        url: window.location.href,
                        activity_name: '5235d9b379e',
                        // 区分不同的活动通过渠道名映射
                        source_channel: getOpenFromByQuery(query),
                        // 表示站外投放来源，da取数只能通过 2 取
                        entry_src: OrderSourceEnum.COOL_PLAN_AD_CHANNEL,
                        ug_adid: getAdidFromByQuery(query),
                    },
                };
            }
            return {
                params: {},
            };
        },
        {
            nameSensitiveOnlyList: [],
        },
    );
}

export const useFmpEffect = () => {
    return inject<FmpEffect>('fmpEffect');
};

export const usessrFmpEffect = () => {
    return inject<FmpEffect>('ssrFmpEffect');
};

export const radarCustomStage = (radar: Radar) => {
    radar?.event(
        {
            name: 'hydrated',
            event_type: 'success',
        },
        {
            duration: new Date().getTime() - performance.timing.navigationStart,
        },
    );
    radar?.customStage('hydrated', {
        sendTimeline: true,
        timestamp: new Date().getTime(),
    });
    performance?.getEntriesByType?.('mark')?.forEach(({ name, startTime }) => {
        radar?.customStage(name, {
            sendTimeline: false,
            timestamp: startTime + performance.timing.navigationStart,
        });
    });
};
